import React, { useMemo, useRef } from 'react'

import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import DynamicHighchart from '~/components/atoms/DynamicHighchart'
import Loader from '~/components/atoms/Loader/Loader'
import PortfolioLozenge from '~/components/atoms/PortfolioLozenge/PortfolioLozenge'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { dateFormat } from '~/utils/dateFormat'
import { numberWithCommas } from '~/utils/formatCurrency'

import MAB100Header from '../MAB100Header/MAB100Header'

import type { PeriodData } from './hooks'
import useHooks, { FilterType } from './hooks'

import styles from './MAB100Chart.module.css'

export interface MAB100ChartProps {
  testID?: string
  chartData?: PeriodData[]
  noPadding?: boolean
  pageBlocks: any
}

function findSmallestSecondValue(arrays: any) {
  return Math.min(...arrays.map((array: any) => array[1]))
}

export default function MAB100Chart({
  testID,
  chartData,
  noPadding = false,
  pageBlocks,
}: MAB100ChartProps) {
  const t = useTranslations('mab100.chart')
  const { filteredData, setCurrentFilter, currentFilter, hideFilters } =
    useHooks(chartData)

  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)

  const today = new Date(new Date())
  const dayOffset = isMobile ? 30 : 10
  today.setDate(today.getDate() - 5)
  today.setDate(today.getDate() - dayOffset)
  const chartComponentRef = useRef(null)

  let minYValue = 60
  if (currentFilter === FilterType.twelveMonths)
    minYValue =
      findSmallestSecondValue(
        chartData?.find((x: any) => x.period === '1 year')?.series
      ) - 5
  if (currentFilter === FilterType.sixmonths)
    minYValue =
      findSmallestSecondValue(
        chartData?.find((x: any) => x.period === '6 month')?.series
      ) - 5
  if (currentFilter === FilterType.thirtydays)
    minYValue =
      findSmallestSecondValue(
        chartData?.find((x: any) => x.period === '30 day')?.series
      ) - 5

  const options = useMemo(() => {
    return {
      title: {
        text: '',
        styledMode: true,
      },
      navigator: {
        enabled: true,
      },
      rangeSelector: {
        selected: 1,
      },
      xAxis: {
        title: { text: '' },
        uniqueNames: true,
        gridLineColor: '#EEE',
        startOnTick: false,
        endOnTick: false,
        minorGridLineWidth: 0,
        type: 'datetime',
        labels: {
          style: {
            fontFamily: 'Noir No1',
            fontWeight: 400,
            fontSize: 14,
            color: '#777777',
          },
        },
        dateTimeLabelFormats: {
          month: {
            main: '%b %Y',
          },
        },
      },
      credits: {
        text: '© MyArtBroker',
      },
      yAxis: {
        title: {
          text: '',
        },
        gridLineColor: '#333333',
        minorTicks: false,
        startOnTick: false,
        endOnTick: true,
        min: minYValue,
        labels: {
          style: {
            fontFamily: 'Noir No1',
            fontWeight: 400,
            fontSize: 14,
            color: '#fff',
          },
          format: '{text}',
        },
      },
      chart: {
        type: 'area',
        zoomType: 'xy',
        height: isMobile ? 280 : 400,
        className: styles.highChartContainer,
        spacingRight: isMobile ? 10 : 5,
        scrollablePlotArea: {
          scrollPositionX: 2,
        },
        backgroundColor: '#0000',
      },
      tooltip: {
        backgroundColor: '#000',
        borderColor: '#000',
        borderWidth: 0,
        style: {
          color: '#fff',
          whitespace: '1',
          textAlign: 'center',
          fontFamily: 'Noir No1',
          fontSize: '14px',
        },
        shadow: false,
        xDateFormat: '%Y-%m-%d',
        formatter: function (this: any) {
          const { x, y } = this
          const dateValue = x as any
          const amountValue = y as number

          const formattedDate = dateFormat(
            '%d/%m/%Y',
            new Date(dateValue).getTime()
          )

          return `${formattedDate} <br /> ${numberWithCommas(amountValue)}`
        },
      },
      plotOptions: {
        series: {
          allowPointSelect: true,
          marker: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
      series: [
        {
          showInLegend: false,
          data: filteredData && filteredData?.series,
          color: '#B3A0E7',
          lineWidth: 1,
          fillColor: {
            linearGradient: {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: 1,
            },
            stops: [
              [0, 'rgba(104, 68, 214, 0.1)'],
              [1, 'rgba(104, 68, 214, 0)'],
            ],
          },
        },
      ],
      exporting: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
    }
  }, [filteredData])

  return (
    <>
      <MAB100Header
        title={'Mab <em>100</em>'}
        total={filteredData?.current}
        change={filteredData?.change}
        pageBlocks={pageBlocks}
      />
      <div
        className={classnames(styles.base, noPadding && styles.noPadding)}
        data-testid={testID}
      >
        <Container gutters={noPadding ? gutterTypes.never : gutterTypes.always}>
          <>
            <div className={styles.chartContainer}>
              <>
                <div
                  className={classnames(
                    styles.chart,
                    !filteredData && styles.chartLoading
                  )}
                >
                  <div className={styles.filterContainer}>
                    {!hideFilters && (
                      <>
                        <PortfolioLozenge
                          theme="dark"
                          title={t('thirtyDays')}
                          isActive={currentFilter === FilterType.thirtydays}
                          onClick={() =>
                            setCurrentFilter(FilterType.thirtydays)
                          }
                          disabled={!filteredData}
                        />
                        <PortfolioLozenge
                          theme="dark"
                          title={t('sixMonths')}
                          isActive={currentFilter === FilterType.sixmonths}
                          onClick={() => setCurrentFilter(FilterType.sixmonths)}
                          disabled={!filteredData}
                        />
                        <PortfolioLozenge
                          theme="dark"
                          title={t('oneYear')}
                          isActive={currentFilter === FilterType.twelveMonths}
                          onClick={() =>
                            setCurrentFilter(FilterType.twelveMonths)
                          }
                          disabled={!filteredData}
                        />
                        <PortfolioLozenge
                          theme="dark"
                          title={t('fiveYears')}
                          isActive={currentFilter === FilterType.fiveYear}
                          onClick={() => setCurrentFilter(FilterType.fiveYear)}
                          disabled={!filteredData}
                        />
                      </>
                    )}
                  </div>
                  {!filteredData && (
                    <div className={styles.chartLoader}>
                      <Loader />
                    </div>
                  )}
                  <DynamicHighchart options={options} ref={chartComponentRef} />
                </div>
              </>
            </div>
          </>
        </Container>
      </div>
    </>
  )
}
